import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import Text from "@/components-legacy/text";
import useStripeIntegrationCreateAccountMutation from "@babackapp/private-api-client/client/use-stripe-integration-create-account";
import useTranslation from "@babackapp/translations/use-translation";
import { Button } from "@babackapp/ui/components/button";
import { CheckCircleIcon, FileWarningIcon, XCircleIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { StripeConfigurationStatus } from "./integration-configuration-tag";
import IntegrationPreview from "./integration-preview";

const StripeAlert = () => {
  const allGood = <StripeIntegrationSuccess />;
  const warning = <StripeIntegrationWarning />;
  const error = <StripeIntegrationError />;

  const stripeConfigurationStatus = useStripeConfigurationStatus();

  return {
    "all-good": allGood,
    warning: warning,
    error: error,
    none: null,
  }[stripeConfigurationStatus];
};

const StripeIntegration = () => {
  const { data: shop } = useCurrentShopSuspense();
  const t = useTranslation();

  const {
    isPending: createStripeAccountLoading,
    mutateAsync: createStripeAccount,
  } = useStripeIntegrationCreateAccountMutation(shop);

  const isLoading = createStripeAccountLoading;
  // TODO: use isLoading to show a spinner
  void isLoading;

  return (
    <div>
      <div className="mt-4">
        <StripeAlert />
      </div>
      <div className="flex justify-between pt-4 items-center">
        {shop.integrations?.stripe?.details_submitted && (
          <Text className="text-sm font-semibold">
            {shop.integrations.stripe.userId}
          </Text>
        )}
        <div>
          <Button onClick={() => createStripeAccount()}>
            {t("settings.integrations.thirdpart.stripe.addStripe")}
          </Button>
        </div>
      </div>
    </div>
  );
};

function StripeIntegrationSuccess() {
  const t = useTranslation();

  return (
    <div className="rounded-md bg-green-50 p-4">
      <div className="flex">
        <div className="flex-shrink-0">
          <CheckCircleIcon
            className="h-5 w-5 text-green-400"
            aria-hidden="true"
          />
        </div>
        <div className="ml-3">
          <h3 className="text-sm font-medium text-green-800">
            {t("settings.integrations.thirdpart.stripe.integrationCompleted")}
          </h3>
          <div className="mt-2 text-sm text-green-700">
            <p>{t("settings.integrations.thirdpart.stripe.successMessage")}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function StripeIntegrationWarning() {
  const t = useTranslation();

  return (
    <div className="rounded-md bg-yellow-50 p-4">
      <div className="flex">
        <div className="flex-shrink-0">
          <FileWarningIcon
            className="h-5 w-5 text-yellow-400"
            aria-hidden="true"
          />
        </div>
        <div className="ml-3">
          <h3 className="text-sm font-medium text-yellow-800">
            {t("settings.integrations.thirdpart.stripe.integrationInProgress")}
          </h3>
          <div className="mt-2 text-sm text-yellow-700">
            <p>
              {t("settings.integrations.thirdpart.stripe.integrationMessage")}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function StripeIntegrationError() {
  const t = useTranslation();

  return (
    <div className="rounded-md bg-red-50 p-4">
      <div className="flex">
        <div className="flex-shrink-0">
          <XCircleIcon className="h-5 w-5 text-red-400" aria-hidden="true" />
        </div>
        <div className="ml-3">
          <h3 className="text-sm font-medium text-red-800">
            {t("settings.integrations.thirdpart.stripe.integrationError")}
          </h3>
          <div className="mt-2 text-sm text-red-700">
            <p>{t("settings.integrations.thirdpart.stripe.errorMessage")}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export function StripeIntegrationPreview() {
  const t = useTranslation();
  const { data: shop } = useCurrentShopSuspense();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const configurationStatus = useStripeConfigurationStatus();

  useEffect(() => {
    const isStripeCallback = location.pathname.endsWith("/integrations/stripe");
    if (isStripeCallback) {
      setOpen(true);
      navigate("..", { replace: true, relative: "path" });
    }
  }, [navigate, location, shop.id]);

  if (!shop || !shop.plan.stripe) return null;

  return (
    <IntegrationPreview
      bgColor="#5433ff"
      textColor="white"
      title="Stripe"
      logo="/integrations/stripe.png"
      description={t("settings.integrations.thirdpart.stripe.description")}
      activated={shop.integrations.stripe.activated}
      badge={configurationStatus}
      seeMoreUrl="https://stripe.com/"
      open={open}
    >
      <StripeIntegration />
    </IntegrationPreview>
  );
}

function useStripeConfigurationStatus(): StripeConfigurationStatus {
  /**
   * TODO: rename the success param if possible
   */
  const { data: shop } = useCurrentShopSuspense();
  // const params = useParams<{ stripeAccountId: string; success?: string }>();
  const params = new URLSearchParams(location.search);
  const successParam = params.get("success");

  if (!shop) return "none";

  const success = successParam === null ? undefined : successParam === "true";

  if (success === undefined && shop.integrations?.stripe?.activated) {
    return shop.integrations.stripe.details_submitted ? "all-good" : "warning";
  }

  if (!shop?.integrations?.stripe) return "none";

  return success ? "all-good" : "error";
}
